import React, { FC, useContext, useMemo } from 'react';

import {
  Button,
  H2,
  Paragraph,
  PasswordInput,
  TextArea,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { usePayment } from 'hooks';
import { LocalizationContext } from 'localization';
import { GET_ACTIVE_PAYMENTS } from 'pages/Payments/schema';

import { IPostPaymentsModalComponentsProps } from '../interfaces';
import s from '../PostPaymentsModal.module.scss';

export const Cancel: FC<IPostPaymentsModalComponentsProps> = ({
  setType,
  payment,
  onClose,
  type,
  hostName,
  refetch,
  statedPoling,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { control, watch, handleSubmit } = useForm({
    defaultValues: {
      comment: '',
      confirm_pass: '',
    },
  });

  const commentState = watch('comment');
  const confirmPassState = watch('confirm_pass');

  const isValid = useMemo(() => {
    return commentState.trim() !== '' && confirmPassState.trim() !== '';
  }, [commentState, confirmPassState]);

  const { cancelPostPayment, postPaymentLoading } = usePayment(
    type === 'payment' ? [GET_ACTIVE_PAYMENTS] : []
  );

  return (
    <>
      {permissions?.has('payment_create') && (
        <Button
          variant="ghost"
          iconName="BackwardIcon"
          className={s.back}
          onClick={setType}
        />
      )}
      <H2 className={s.title}>{t('post_payment_receipt.cancel.title')}</H2>
      <Paragraph color="secondary" className={s.description}>
        {t('post_payment_receipt.cancel.description')}
      </Paragraph>

      <form
        onSubmit={handleSubmit((field) => {
          cancelPostPayment({
            processPostPaymentId: payment.id,
            comment: field.comment,
            confirm_pass: field.confirm_pass,
            type,
            hostName,
          })
            .then(() => {
              refetch?.();
              statedPoling?.();
              onClose();
            })
            .catch((error) => {
              console.log(error);
            });
        })}
        className={s.form}
      >
        <Controller
          control={control}
          name="comment"
          render={({ field, fieldState: { error } }) => (
            <TextArea
              title={t('post_payment_receipt.cancel.placeholder')}
              placeholder=""
              error={error?.message}
              {...field}
            />
          )}
          rules={{ required: t('errors.required') }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <PasswordInput
              {...field}
              error={error?.message}
              title={t('password')}
            />
          )}
          name="confirm_pass"
          control={control}
          defaultValue=""
          rules={{ required: t('errors.required') }}
        />
        <Button
          type="submit"
          fullWidth
          disabled={!isValid || postPaymentLoading}
          className={s.form__button}
        >
          {t('post_payment_receipt.cancel.button')}
        </Button>
      </form>
    </>
  );
};
